// The storefront's only door to the cart. The cart lives on the server (Mongo, with the guest identity in
// an httpOnly cookie), so nothing here reads or writes browser storage. Plan IRO-3676 sections 3 and 7.
//
//   get()            GET    /controllers/cart
//   put(cart)        PUT    /controllers/cart          lines / certificates / coupon / certificate / contact + version
//   clear()          DELETE /controllers/cart
//   merge()          POST   /controllers/cart/merge    after login: fold the guest cart into the member's
//   importLegacy(c)  POST   /controllers/cart/import   one-time import of a browser-stored cart
//   config()         GET    /controllers/cart/config   the cartStorage rollout flag
//
// No Vue, Pinia or Mango imports: node tests load this file directly. The HTTP client, API base and auth
// headers are injected (main.js wires the real ones).

export const CART_CSRF_HEADERS = Object.freeze({ 'X-Requested-With': 'XMLHttpRequest' })

// What PUT accepts. Anything else (id, bcId, totals, author, price fields) is not sent at all.
const PUT_FIELDS = ['cartItems', 'certificates', 'coupon', 'certificate', 'kitId', 'email', 'firstName', 'lastName', 'version']

function lineForServer(line) {
	return {
		quantity: line.quantity,
		item: line.item?.id || line.item,
		variant: line.variant?.id || line.variant || undefined,
		optional: line.optional || null,
		...(line.optionSelections?.length > 0 && { optionSelections: line.optionSelections }),
		queryID: line.queryID || null,
		...(line.group != null && { group: line.group }),
	}
}

/** The request body for PUT /cart: ids and quantities only. The server hydrates and prices everything. */
export function cartPutBody(cart, version) {
	const body = {}
	if (Array.isArray(cart?.cartItems)) body.cartItems = cart.cartItems.map(lineForServer)
	if (Array.isArray(cart?.certificates)) body.certificates = cart.certificates
	if ('coupon' in (cart || {})) body.coupon = cart.coupon?.code || ''
	if ('certificate' in (cart || {})) body.certificate = cart.certificate?.code || ''
	for (const key of ['kitId', 'email', 'firstName', 'lastName']) if (cart?.[key] != null) body[key] = cart[key]
	if (version != null) body.version = version
	return Object.fromEntries(Object.entries(body).filter(([key]) => PUT_FIELDS.includes(key)))
}

/** Normalises axios success and failure into one shape so callers branch on `code`, not on exceptions. */
function settle(promise) {
	return promise.then(
		(response) => ({ ok: true, status: response?.status ?? 200, ...(response?.data || {}) }),
		(error) => {
			const data = error?.response?.data
			const status = Number(error?.response?.status) || 0
			return { ok: false, status, network: status === 0, code: data?.code || (status === 0 ? 'network_error' : 'error'), message: data?.message || error?.message, cart: data?.cart, productBcId: data?.productBcId, productName: data?.productName, error }
		},
	)
}

// nativeToken is only passed by the bundled native shell (capacitor://localhost), whose cross-site calls never
// carry the SameSite=Lax cookie: { get(), set(token), clear() } backed by native secure storage, NOT web
// storage. The guest token is an opaque credential, not cart data. Web builds omit it. IRO-3895.
export function createCartApi({ http, getApi, getHeaders = () => ({}), nativeToken = null } = {}) {
	const request = async (method, path, data) => {
		const api = getApi()
		const stored = nativeToken ? await nativeToken.get() : ''
		const result = await settle(
			http({
				method,
				url: `${api}/controllers/cart${path}`,
				...(data !== undefined && { data }),
				withCredentials: true,
				headers: { ...getHeaders(), ...CART_CSRF_HEADERS, ...(nativeToken && { 'X-Ihh-Native': '1', ...(stored && { 'X-Cart-Token': stored }) }) },
			}),
		)
		if (nativeToken && result.nativeToken) await nativeToken.set(result.nativeToken)
		else if (nativeToken && result.clearNativeToken) await nativeToken.clear()
		return result
	}
	return {
		get: () => request('get', ''),
		put: (cart, version) => request('put', '', cartPutBody(cart, version)),
		clear: () => request('delete', ''),
		merge: () => request('post', '/merge', {}),
		importLegacy: (cart) => request('post', '/import', { cart }),
		config: () => request('get', '/config'),
	}
}

let instance = null

/** main.js calls this once with axios, the API base and the bearer-header reader. */
export function configureCartApi(options) {
	instance = createCartApi(options)
	return instance
}

export const cartApi = {
	get: (...args) => instance.get(...args),
	put: (...args) => instance.put(...args),
	clear: (...args) => instance.clear(...args),
	merge: (...args) => instance.merge(...args),
	importLegacy: (...args) => instance.importLegacy(...args),
	config: (...args) => instance.config(...args),
}
